import { List, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

/**
 * Error Handling - React 2025 Best Practices
 *
 * Inspired by Next.js, Remix, and TanStack Query patterns.
 * Key principle: Developers throw standard Error objects.
 * Transformation to user-friendly messages happens at boundaries/handlers.
 */

/**
 * Error pattern matchers
 */
const errorPatterns = [
  {
    keywords: ['fetch', 'network', 'connection'],
    message: 'Unable to connect. Please check your internet connection and try again.',
  },
  {
    keywords: ['unauthorized', '401'],
    message: 'Please log in to continue.',
  },
  {
    keywords: ['forbidden', '403', 'permission'],
    message: "You don't have permission to access this resource.",
  },
  {
    keywords: ['not found', '404'],
    message: 'The requested resource was not found.',
  },
  {
    keywords: ['rate limit', '429', 'too many'],
    message: 'Too many requests. Please wait a moment and try again.',
  },
  {
    keywords: ['invalid', 'validation', '400', 'status code 400'],
    message: 'The data you entered is not valid. Please check your input and try again.',
  },
  {
    keywords: ['500', 'server error', '503', '502', 'internal_error', 'internal error'],
    message: 'A server error occurred. Please try again later.',
  },
];

/**
 * Check if message contains technical jargon
 */
const hasTechnicalJargon = (message: string): boolean => {
  const jargon = ['undefined', 'null', 'typeerror', 'valid json'];
  return jargon.some(term => message.includes(term));
};

/**
 * Transform error message to user-friendly format
 *
 * This is where technical errors become user-friendly.
 * Add patterns to errorPatterns array to handle different scenarios.
 */
export const toUserFriendlyMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'Something went wrong. Please try again.';
  }

  const message = error.message.toLowerCase();

  // Check against known patterns
  for (const pattern of errorPatterns) {
    if (pattern.keywords.some(keyword => message.includes(keyword))) {
      return pattern.message;
    }
  }

  // Return original message if it looks user-friendly
  if (!hasTechnicalJargon(message)) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

/**
 * Show an error notification
 *
 * Transforms technical errors into user-friendly messages automatically.
 * Optionally provide context to make the error more actionable.
 *
 * @param error - The error object to display
 * @param context - Optional context describing what operation failed (e.g., "Failed to refresh search data")
 *
 * @example
 * // Basic usage - generic error
 * try {
 *   await fetchData();
 * } catch (error) {
 *   showErrorNotification(error);
 * }
 *
 * @example
 * // With context - specific operation
 * try {
 *   await refreshGlobalSearchData();
 * } catch (error) {
 *   showErrorNotification(error, 'Failed to refresh search data');
 * }
 *
 * @example
 * // In mutations (no context needed - mutation name provides it)
 * const mutation = useMutation({
 *   mutationFn: saveData,
 *   onError: showErrorNotification,
 * });
 */
export const showErrorNotification = (error: unknown, context?: unknown) => {
  const userMessage = toUserFriendlyMessage(error);
  const contextMessage = typeof context === 'string' ? context : undefined;

  if (import.meta.env.DEV) {
    console.error('Error:', error);
  }

  notifications.show({
    color: 'red',
    message: contextMessage ? `${contextMessage}: ${userMessage}` : userMessage,
    title: 'Error',
  });
};

/**
 * Show a success notification
 *
 * @example
 * showSuccessNotification('Record created successfully');
 */
export const showSuccessNotification = (message: string) => {
  notifications.show({
    color: 'green',
    message,
    title: 'Success',
  });
};

/**
 * Show a validation-error notification listing multiple specific problems (e.g. missing
 * required fields) rather than a single generic message — used when a user action is blocked
 * pending fixes, so they know exactly what to correct instead of just that "something's wrong".
 *
 * @example
 * showValidationErrorNotification('Cannot test formulas. Please complete all required fields.', [
 *   'Moisture → Contracted moisture (%)',
 *   'Sulfur → Lower Bound',
 * ]);
 */
export const showValidationErrorNotification = (title: string, items: string[]) => {
  notifications.show({
    autoClose: 8000,
    color: 'red',
    // A stable id means re-triggering the same blocked action (e.g. clicking "Test" again while
    // fields are still missing) updates this one notification instead of stacking a new one on
    // top of a still-open previous one.
    id: 'validation-error',
    message: (
      <Stack gap={4}>
        <Text fz="sm">Required fields missing:</Text>
        <List fz="sm" spacing={2}>
          {items.map(item => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>
      </Stack>
    ),
    title,
  });
};

/**
 * Show an info notification
 *
 * @example
 * showInfoNotification('No changes to save');
 */
export const showInfoNotification = (message: string) => {
  notifications.show({
    color: 'blue',
    message,
    title: 'Info',
  });
};

/**
 * Show a warning notification — for preconditions the user hasn't met yet (e.g. "select
 * something first"), as opposed to showErrorNotification's "what you entered is invalid".
 *
 * @example
 * showWarningNotification('Select at least one item first.');
 */
export const showWarningNotification = (message: string) => {
  notifications.show({
    color: 'yellow',
    message,
    title: 'Warning',
  });
};
